import { useState } from 'react';
import { ExternalLink, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/Button';
import { Input, Label } from '@/components/ui/Input';
import { StatusBadge } from '@/components/ui/Badge';
import { Skeleton } from '@/components/ui/Skeleton';
import { DnsRecordsCard } from './DnsRecordsCard';
import { useDomainDetail, useDomainActions } from '@/hooks/useDispatcher';

interface Props {
  domainId: string | null;
  workspaceId: string;
  onClose: () => void;
}

export function DomainDetailDrawer({ domainId, workspaceId, onClose }: Props) {
  const { data: detail, isLoading } = useDomainDetail(domainId, workspaceId);
  const { recheckDns, configureForwarder, addRedirect } = useDomainActions(workspaceId);

  const [forwarderTarget, setForwarderTarget] = useState('');
  const [redirectUrl, setRedirectUrl] = useState('');

  if (!domainId) return null;

  async function handleConfigureForwarder() {
    if (!domainId || !forwarderTarget.trim()) return;
    await configureForwarder.mutateAsync({ domainId, target: forwarderTarget.trim() });
    toast.success('Forwarder configuré');
    setForwarderTarget('');
  }

  async function handleAddRedirect() {
    if (!domainId || !redirectUrl.trim()) return;
    await addRedirect.mutateAsync({ domainId, target_url: redirectUrl.trim(), override: true });
    toast.success('Redirect créé');
    setRedirectUrl('');
  }

  return (
    <>
      <div
        className="fixed inset-0 z-40 bg-brand-dark/40"
        onClick={onClose}
        aria-hidden
      />
      <div
        className={cn(
          'fixed top-0 right-0 z-50 h-full w-full max-w-xl bg-white shadow-xl flex flex-col',
          'transition-transform duration-200',
        )}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-brand-border">
          <div>
            <h3 className="font-heading font-bold text-brand-dark text-base">
              {detail?.domain ?? '…'}
            </h3>
            {detail && (
              <a
                href={`https://${detail.domain}`}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-brand-primary hover:underline flex items-center gap-1 mt-0.5"
              >
                Visiter <ExternalLink size={10} />
              </a>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Fermer"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          {isLoading || !detail ? (
            <Skeleton className="h-40 w-full" />
          ) : (
            <>
              {/* DNS Records */}
              <DnsRecordsCard
                domain={detail}
                onRecheck={() =>
                  recheckDns
                    .mutateAsync(detail.id)
                    .then(() => toast.success('Recheck lancé'))
                    .catch(() => toast.error('Erreur recheck'))
                }
                recheckPending={recheckDns.isPending}
              />

              {/* IONOS Forwarder */}
              <div className="rounded-lg border border-brand-border bg-white p-4 space-y-3">
                <h4 className="text-sm font-semibold text-brand-dark">IONOS Forwarder</h4>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-gray-500">Statut :</span>
                  <span
                    className={
                      detail.ionos_forwarder_active
                        ? 'text-green-600 font-medium'
                        : 'text-red-500'
                    }
                  >
                    {detail.ionos_forwarder_active ? '✓ Actif' : '✗ Non configuré'}
                  </span>
                </div>
                {detail.ionos_forwarder_target && (
                  <p className="text-xs font-mono text-gray-500">{detail.ionos_forwarder_target}</p>
                )}
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label>Cible forwarder</Label>
                    <Input
                      value={forwarderTarget}
                      onChange={(e) => setForwarderTarget(e.target.value)}
                      placeholder="https://track.yourdomain.com"
                    />
                  </div>
                  <Button
                    onClick={handleConfigureForwarder}
                    loading={configureForwarder.isPending}
                    disabled={!forwarderTarget.trim()}
                  >
                    Configurer
                  </Button>
                </div>
              </div>

              {/* Redirects */}
              <div className="rounded-lg border border-brand-border bg-white p-4 space-y-3">
                <h4 className="text-sm font-semibold text-brand-dark">Redirections</h4>
                {detail.redirects.length === 0 ? (
                  <p className="text-sm text-gray-400">Aucune redirection</p>
                ) : (
                  <div className="space-y-1">
                    {detail.redirects.map((r) => (
                      <div
                        key={r.id}
                        className="flex items-center justify-between text-xs py-1 border-b border-brand-border last:border-0"
                      >
                        <span className="font-mono text-gray-600 truncate max-w-xs">
                          {r.target_url}
                        </span>
                        <StatusBadge status={r.active ? 'active' : 'inactive'} />
                      </div>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <div className="flex-1">
                    <Label>Nouvelle cible</Label>
                    <Input
                      value={redirectUrl}
                      onChange={(e) => setRedirectUrl(e.target.value)}
                      placeholder="https://target.com"
                    />
                  </div>
                  <Button
                    onClick={handleAddRedirect}
                    loading={addRedirect.isPending}
                    disabled={!redirectUrl.trim()}
                  >
                    Ajouter
                  </Button>
                </div>
              </div>

              {/* Accounts */}
              {detail.accounts.length > 0 && (
                <div className="rounded-lg border border-brand-border bg-white p-4 space-y-2">
                  <h4 className="text-sm font-semibold text-brand-dark">
                    Comptes email ({detail.accounts.length})
                  </h4>
                  <div className="space-y-1 max-h-40 overflow-y-auto">
                    {detail.accounts.map((a) => (
                      <div
                        key={a.id}
                        className="flex items-center justify-between text-xs py-1"
                      >
                        <span className="text-gray-700">{a.email}</span>
                        <StatusBadge status={a.status} />
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
}
